import DateFormat from '@/Enums/Format/DateFormat.enum';
import type { ICampaignManagementCampaign } from '@/Interfaces/CampaignManagement/Campaign.interface';
import type {
  ICampaignManagementLineItem,
  ILineItemCreative,
  ILineItemForm,
} from '@/Interfaces/CampaignManagement/LineItem.interface';
import type { IBasicInfoRow } from '@/Services/CampaignManagement/LineItemSummary.service';
import LineItemSummaryService from '@/Services/CampaignManagement/LineItemSummary.service';
import { BeeswaxPacingLabel } from '@/constants/lineItems/beeswaxPacingLabels';
import { lineItemApiDataToFormStructure } from '@/helpers/campaignManagement/lineItemForm';
import { dateToFormat } from '@/helpers/valueFormatter/dateFormatter.helpers';
import { numberToPercent } from '@/helpers/valueFormatter/numberFormatter.helpers';
import type { MomentInput } from 'moment';
import moment from 'moment';

/** Publisher and geo lists run to thousands of entries on zip targeting; cells stay readable capped. */
export const MAX_LIST_VALUES = 100;

export interface ICampaignExportLineItemRow {
  campaignName: string;
  campaignId: number;
  campaignStart: string;
  campaignEnd: string;
  lineItemName: string;
  lineItemId: number;
  lineItemStart: string;
  lineItemEnd: string;
  impressionsBooked: number | null;
  bidCpm: number | null;
  soldCpm: number | null;
  channel: string;
  devices: string;
  product: string;
  pacing: string;
  dailyImpressionCap: number | null;
  publishers: string;
  geo: string;
  audiences: string;
  daypart: string;
  creativeNames: string;
  creativeDates: string;
  creativeWeights: string;
}

export interface ICampaignExportData {
  campaignName: string;
  lineItemRows: ICampaignExportLineItemRow[];
  includeBidCpm: boolean;
}

interface IBuildOptions {
  /** Whitelabels that hide bid CPM must not leak buying rates into a client-facing document. */
  showBidCpm: boolean;
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toDateTimeText = (date: MomentInput): string => dateToFormat(date, DateFormat.NumericMonthDayYearTime, '');

const toDateText = (date: MomentInput): string => dateToFormat(date, DateFormat.NumericMonthDayYear, '');

const capValues = (values: string[]): string[] => {
  if (values.length <= MAX_LIST_VALUES) {
    return values;
  }
  return [...values.slice(0, MAX_LIST_VALUES), `+${values.length - MAX_LIST_VALUES} more`];
};

const infoRowValueToText = (value: IBasicInfoRow['value']): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';

/**
 * `LineItemSummaryService` emits merged-cell rows: only the first row of a group carries its
 * label. Regroup them so a group becomes one line, `Label: value, value`.
 */
const infoRowsToLines = (rows: IBasicInfoRow[]): string[] => {
  const groups: { label: string; values: string[] }[] = [];

  rows.forEach(({ label, value }) => {
    if (label || !groups.length) {
      groups.push({ label: label ?? '', values: [] });
    }
    const text = infoRowValueToText(value);
    if (text) {
      groups[groups.length - 1].values.push(text);
    }
  });

  return groups
    .filter(({ values }) => values.length)
    .map(({ label, values }) => {
      const joined = capValues(values).join(', ');
      return label ? `${label}: ${joined}` : joined;
    });
};

const buildGeoText = (formValues: ILineItemForm): string => {
  const { included, excluded, radius } = LineItemSummaryService.getGeoInfo(formValues);

  const lines: string[] = [];

  // Mirrors the Summary tab: a radius fence replaces inclusions, exclusions always show.
  if (radius.length) {
    lines.push(`Radius Targeting — ${infoRowsToLines(radius).join(' — ')}`);
  } else {
    lines.push(...infoRowsToLines(included).map(line => `Included ${line}`));
  }
  lines.push(...infoRowsToLines(excluded).map(line => `Excluded ${line}`));

  return lines.join('\n');
};

const buildCreativeDatesText = (creatives: ILineItemCreative[]): string =>
  capValues(
    creatives.map(({ startDate, endDate }) => {
      const start = toDateText(startDate);
      const end = toDateText(endDate);
      return start || end ? `${start || '—'} - ${end || '—'}` : 'Line Item flight';
    }),
  ).join('\n');

const buildCreativeWeightsText = (creatives: ILineItemCreative[]): string =>
  capValues(
    creatives.map(({ associationWeightPct }) => {
      const pct = toNumber(associationWeightPct);
      return pct === null ? '—' : numberToPercent(pct);
    }),
  ).join('\n');

const getCampaignDates = (
  campaign: ICampaignManagementCampaign,
  lineItems: ICampaignManagementLineItem[],
): { start: string; end: string } => {
  // Beeswax keeps campaign dates as min/max of its line items, but the sync can lag a freshly
  // created campaign, so fall back to the line items we already have.
  const validDates = (dates: MomentInput[]) => dates.map(date => moment.utc(date)).filter(date => date.isValid());

  const starts = validDates(lineItems.map(({ start_date }) => start_date));
  const ends = validDates(lineItems.map(({ end_date }) => end_date));

  return {
    start: toDateTimeText(campaign.start_date || (starts.length ? moment.min(starts) : null)),
    end: toDateTimeText(campaign.end_date || (ends.length ? moment.max(ends) : null)),
  };
};

const buildLineItemRow = (
  campaign: ICampaignManagementCampaign,
  campaignDates: { start: string; end: string },
  lineItem: ICampaignManagementLineItem,
  formValues: ILineItemForm,
  options: IBuildOptions,
): ICampaignExportLineItemRow => {
  const creatives = formValues.creatives ?? [];

  return {
    campaignName: campaign.name ?? '',
    campaignId: campaign.id,
    campaignStart: campaignDates.start,
    campaignEnd: campaignDates.end,
    lineItemName: formValues.name ?? '',
    lineItemId: lineItem.id,
    lineItemStart: toDateTimeText(formValues.startDate),
    lineItemEnd: toDateTimeText(formValues.endDate),
    impressionsBooked: toNumber(formValues.impressionsBooked),
    bidCpm: options.showBidCpm ? toNumber(formValues.bidCpm) : null,
    soldCpm: toNumber(formValues.soldCpm),
    channel: formValues.channel?.label ?? '',
    devices: (formValues.devices ?? []).map(({ label }) => label).join(', '),
    product: formValues.product?.name ?? '',
    pacing: BeeswaxPacingLabel[formValues.pacing] ?? '',
    dailyImpressionCap: toNumber(formValues.dailyBudget),
    publishers: capValues((lineItem.publishers ?? []).map(({ name }) => name).filter(Boolean)).join('\n'),
    geo: buildGeoText(formValues),
    audiences: infoRowsToLines(LineItemSummaryService.getAudienceInfo(formValues)).join('\n'),
    daypart: infoRowsToLines(LineItemSummaryService.getDaypartInfo(formValues)).join('\n'),
    creativeNames: capValues(creatives.map(({ name }) => name ?? '')).join('\n'),
    creativeDates: buildCreativeDatesText(creatives),
    creativeWeights: buildCreativeWeightsText(creatives),
  };
};

/**
 * Builds the QA export (flank #4145) from the campaign already in the store — the campaign detail
 * response carries every field the export needs for all line items, expanded or not.
 */
export const buildCampaignExportData = (
  campaign: ICampaignManagementCampaign,
  options: IBuildOptions,
): ICampaignExportData => {
  const lineItems = (campaign.lineItems ?? []).filter(({ is_archived }) => !is_archived);
  const campaignDates = getCampaignDates(campaign, lineItems);

  return {
    campaignName: campaign.name ?? '',
    includeBidCpm: options.showBidCpm,
    lineItemRows: lineItems.map(lineItem => {
      const formValues = lineItemApiDataToFormStructure(lineItem);
      // `daypart` is nullable and `geo` can arrive half-populated on imported Line Items; the
      // summary formatters index into both.
      const safeFormValues = {
        ...formValues,
        daypart: formValues.daypart ?? {},
        geo: { included: formValues.geo?.included ?? [], excluded: formValues.geo?.excluded ?? [] },
      } as ILineItemForm;

      return buildLineItemRow(campaign, campaignDates, lineItem, safeFormValues, options);
    }),
  };
};
